import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  PhonelinkLock as AuthenticatorIcon,
  QrCode2 as QrCodeIcon,
  CheckCircle as CheckIcon,
  ContentCopy as CopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  VpnKey as BackupIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import authService from 'shared/services/authService';
import api from 'shared/services/apiClient';
import { useAuth } from 'shared/contexts/AuthContext';

interface UserTwoFactorSetupProps {
  onSetupComplete?: () => void;
}

type SetupStep = 'status' | 'scan' | 'verify' | 'complete';

interface TwoFactorStatus {
  totpEnabled: boolean;
  companyRequires2FA: boolean;
  requiredMethod?: 'email' | 'authenticator';
  isInGracePeriod: boolean;
  gracePeriodEndsAt?: string;
}

export const UserTwoFactorSetup: React.FC<UserTwoFactorSetupProps> = ({
  onSetupComplete,
}) => {
  const { user } = useAuth();
  // Platform users (super_admin, safety_staff) don't belong to any
  // company, so no company policy forces an authenticator on them.
  // Give them — and only them — the explicit chooser between email and
  // authenticator. Other roles keep the company-driven info screen.
  // AuthContext doesn't persist `globalRole` separately — it collapses
  // it into `user.role` via `deriveEffectiveRole`. For platform users
  // the effective role IS their global role, so checking `user.role`
  // is correct here.
  const isPlatformUser =
    user?.role === 'super_admin' || user?.role === 'safety_staff';

  // Status state
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [statusError, setStatusError] = useState<string | null>(null);

  // Setup flow state
  const [step, setStep] = useState<SetupStep>('status');
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [manualKey, setManualKey] = useState<string>('');
  const [showManualKey, setShowManualKey] = useState(false);
  const [verificationCode, setVerificationCode] = useState<string[]>(Array(6).fill(''));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Load 2FA status
  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setLoadingStatus(true);
    setStatusError(null);
    try {
      const result = await authService.getUserTwoFactorStatus();
      setStatus(result);
    } catch (err: unknown) {
      setStatusError(err instanceof Error ? err.message : 'Error al cargar estado');
    } finally {
      setLoadingStatus(false);
    }
  };

  // Start setup - get QR code
  const handleStartSetup = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await authService.startUserTotpSetup();
      setQrCodeUrl(result.qrCodeUrl);
      setManualKey(result.manualEntryKey);
      setStep('scan');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al iniciar configuración');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle code input
  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...verificationCode];
    newCode[index] = value.slice(-1);
    setVerificationCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...verificationCode];
    for (let i = 0; i < pasted.length; i++) {
      newCode[i] = pasted[i];
    }
    setVerificationCode(newCode);
    if (pasted.length === 6) {
      inputRefs.current[5]?.focus();
    }
  };

  // Verify setup
  const handleVerify = async () => {
    const code = verificationCode.join('');
    if (code.length !== 6) {
      setError('Ingresa el código de 6 dígitos');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      await authService.verifyUserTotpSetup(code);
      setStep('complete');
      loadStatus();
      onSetupComplete?.();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Código inválido');
      setVerificationCode(Array(6).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  // Format grace period date
  const formatGracePeriodEnd = (dateStr?: string): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-CR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return '';
    }
  };

  // Render loading state
  if (loadingStatus) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Render error state
  if (statusError) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {statusError}
          </Alert>
          <Button onClick={loadStatus}>Reintentar</Button>
        </CardContent>
      </Card>
    );
  }

  // Method-chooser branch. Restricted to platform users (super_admin
  // and safety_staff). Email is always available as the baseline; the
  // authenticator is opt-in. For company users we fall through to the
  // company-driven info screen further down.
  if (
    isPlatformUser &&
    status?.requiredMethod !== 'authenticator' &&
    step === 'status'
  ) {
    const totpActive = !!status?.totpEnabled;
    return (
      <>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <AuthenticatorIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
              <Box>
                <Typography variant="h6">Método de verificación en dos pasos</Typography>
                <Typography variant="body2" color="text.secondary">
                  Elija cómo recibir el código de verificación al iniciar sesión.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
              {/* Email option */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderColor: !totpActive ? 'primary.main' : 'divider',
                  borderWidth: !totpActive ? 2 : 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Correo electrónico
                  </Typography>
                  {!totpActive && (
                    <Chip
                      icon={<CheckIcon />}
                      label="Activo"
                      color="success"
                      size="small"
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Recibe un código de 6 dígitos en su correo cada vez que inicia sesión.
                  Disponible por defecto.
                </Typography>
              </Paper>

              {/* Authenticator option */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderColor: totpActive ? 'primary.main' : 'divider',
                  borderWidth: totpActive ? 2 : 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Aplicación autenticadora
                  </Typography>
                  {totpActive ? (
                    <Chip
                      icon={<CheckIcon />}
                      label="Activo"
                      color="success"
                      size="small"
                    />
                  ) : (
                    <Chip label="Más seguro" color="primary" size="small" variant="outlined" />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Genera códigos en su dispositivo con Google Authenticator, Authy u otra app
                  compatible. Funciona sin conexión y es resistente a phishing.
                </Typography>
                {!totpActive && (
                  <Button
                    variant="contained"
                    onClick={handleStartSetup}
                    disabled={isLoading}
                    sx={{ mt: 1, alignSelf: 'flex-start' }}
                  >
                    {isLoading ? 'Iniciando…' : 'Configurar autenticador'}
                  </Button>
                )}
                {totpActive && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    Su autenticador está configurado. Use el código de su aplicación al iniciar
                    sesión.
                  </Alert>
                )}
              </Paper>
            </Box>

            {error ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            ) : null}
          </CardContent>
        </Card>
        <BackupCodesSection />
      </>
    );
  }

  // Non-platform users (company_admin, client_*, security) whose company
  // hasn't required the authenticator: keep the original info-only
  // screen. They don't get the email/authenticator chooser — that's
  // platform-user exclusive.
  if (
    !isPlatformUser &&
    status?.requiredMethod !== 'authenticator' &&
    step === 'status'
  ) {
    return (
      <>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <AuthenticatorIcon sx={{ fontSize: 32, mr: 2, color: 'text.secondary' }} />
              <Box>
                <Typography variant="h6">Autenticación por Aplicación</Typography>
                <Chip
                  label="No requerido"
                  color="default"
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              Su empresa utiliza verificación por correo electrónico. No es necesario configurar
              una aplicación de autenticación.
            </Alert>

            {status?.totpEnabled && (
              <Alert severity="warning">
                Tiene un autenticador configurado pero su empresa no lo requiere actualmente.
                Se utilizará verificación por correo electrónico para iniciar sesión.
              </Alert>
            )}
          </CardContent>
        </Card>
        <BackupCodesSection />
      </>
    );
  }

  // Render status view (TOTP enabled and company requires it)
  if (status?.totpEnabled && status?.requiredMethod === 'authenticator' && step === 'status') {
    return (
      <>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <AuthenticatorIcon sx={{ fontSize: 32, mr: 2, color: 'success.main' }} />
              <Box>
                <Typography variant="h6">Autenticador Configurado</Typography>
                <Chip
                  icon={<CheckIcon />}
                  label="Activo"
                  color="success"
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Box>

            <Alert severity="success" sx={{ mb: 3 }}>
              Su aplicacion de autenticacion esta configurada correctamente. Necesitara el codigo
              de su aplicacion cada vez que inicie sesion.
            </Alert>

            <Alert severity="info" sx={{ mt: 2 }}>
              Si necesita desactivar o reconfigurar su autenticador, contacte al administrador de su
              empresa.
            </Alert>
          </CardContent>
        </Card>
        <BackupCodesSection />
      </>
    );
  }

  // Render setup flow
  const steps = ['Escanear QR', 'Verificar'];
  const activeStep = step === 'scan' ? 0 : step === 'verify' || step === 'complete' ? 1 : -1;

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <AuthenticatorIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
          <Box>
            <Typography variant="h6">Autenticación por Aplicación</Typography>
            <Typography variant="body2" color="text.secondary">
              Configura Google Authenticator, Authy u otra app compatible
            </Typography>
          </Box>
        </Box>

        {/* Grace period warning */}
        {status?.isInGracePeriod && status.requiredMethod === 'authenticator' && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="medium">
              Período de gracia activo
            </Typography>
            <Typography variant="body2">
              Su empresa requiere autenticación por aplicación. Tiene hasta el{' '}
              <strong>{formatGracePeriodEnd(status.gracePeriodEndsAt)}</strong> para configurarla.
              Después de esa fecha, no podrá iniciar sesión sin el autenticador.
            </Typography>
          </Alert>
        )}

        {/* Company requires authenticator */}
        {status?.companyRequires2FA && status.requiredMethod === 'authenticator' && !status.isInGracePeriod && !status.totpEnabled && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Tu empresa requiere autenticación por aplicación. Configúrala para continuar usando el
            sistema.
          </Alert>
        )}

        {/* Setup not started */}
        {step === 'status' && (
          <Box>
            <List>
              <ListItem>
                <ListItemIcon>
                  <QrCodeIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Más seguridad"
                  secondary="Proteja su cuenta con un segundo factor de autenticación"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <AuthenticatorIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Aplicaciones compatibles"
                  secondary="Google Authenticator, Authy, Microsoft Authenticator, 1Password"
                />
              </ListItem>
            </List>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              variant="contained"
              onClick={handleStartSetup}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : <AuthenticatorIcon />}
              fullWidth
              sx={{ mt: 2 }}
            >
              {isLoading ? 'Iniciando...' : 'Configurar Autenticador'}
            </Button>
          </Box>
        )}

        {/* Setup flow */}
        {step !== 'status' && (
          <>
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Step 1: Scan QR */}
            {step === 'scan' && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Escanee este código QR con su aplicación de autenticación
                </Typography>

                {qrCodeUrl && (
                  <Box
                    component="img"
                    src={qrCodeUrl}
                    alt="Código QR"
                    sx={{
                      width: 200,
                      height: 200,
                      mx: 'auto',
                      mb: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  />
                )}

                <Divider sx={{ my: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    o ingrese el código manualmente
                  </Typography>
                </Divider>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      px: 2,
                      py: 1,
                      fontFamily: 'monospace',
                      fontSize: '1rem',
                      letterSpacing: 2,
                    }}
                  >
                    {showManualKey ? manualKey : '••••••••••••••••'}
                  </Paper>
                  <Tooltip title={showManualKey ? 'Ocultar' : 'Mostrar'}>
                    <IconButton onClick={() => setShowManualKey(!showManualKey)} sx={{ ml: 1 }}>
                      {showManualKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copiar">
                    <IconButton
                      onClick={() => {
                        navigator.clipboard.writeText(manualKey);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      sx={{ ml: 0.5 }}
                    >
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                {copied && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Copiado al portapapeles
                  </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      // Drop back to the chooser. We don't call the BE
                      // disable endpoint here — the temporary TOTP secret
                      // (tempTotpSecret) lives on the user doc and is
                      // overwritten the next time they hit "Configurar
                      // autenticador". Cleanup happens server-side when
                      // verifyTOTPSetup is called (or when the temp
                      // secret expires).
                      setStep('status');
                      setError(null);
                      setVerificationCode(Array(6).fill(''));
                      setQrCodeUrl('');
                      setManualKey('');
                      setShowManualKey(false);
                    }}
                    sx={{ flex: 1 }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setStep('verify')}
                    sx={{ flex: 1 }}
                  >
                    Continuar
                  </Button>
                </Box>
              </Box>
            )}

            {/* Step 2: Verify */}
            {step === 'verify' && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Ingrese el código de 6 dígitos de su aplicación
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 3 }}>
                  {verificationCode.map((digit, index) => (
                    <TextField
                      key={index}
                      inputRef={(el) => (inputRefs.current[index] = el)}
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : undefined}
                      inputProps={{
                        maxLength: 1,
                        'aria-label': `Dígito ${index + 1} de 6`,
                        style: {
                          textAlign: 'center',
                          fontSize: '1.5rem',
                          fontWeight: 'bold',
                          width: '2rem',
                        },
                      }}
                      sx={{ width: 50 }}
                    />
                  ))}
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button variant="outlined" onClick={() => setStep('scan')} sx={{ flex: 1 }}>
                    Atrás
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleVerify}
                    disabled={isLoading || verificationCode.join('').length !== 6}
                    sx={{ flex: 1 }}
                    startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
                  >
                    {isLoading ? 'Verificando...' : 'Verificar'}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Complete */}
            {step === 'complete' && (
              <Box sx={{ textAlign: 'center' }}>
                <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                  ¡Configuración Completa!
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Tu aplicación de autenticación está lista. La necesitarás cada vez que inicies
                  sesión.
                </Typography>
                <Button variant="outlined" onClick={() => setStep('status')}>
                  Ver Estado
                </Button>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Backup Codes Section — shown on the security page
const BackupCodesSection: React.FC = () => {
  const [status, setStatus] = useState<{ hasBackupCodes: boolean; remaining: number; total: number; generatedAt: string | null } | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showCodes, setShowCodes] = useState(false);
  const [error, setError] = useState('');

  const loadStatus = async () => {
    try {
      const res = await api.get<any>('/auth/2fa/backup-codes/status');
      if (res.success) setStatus(res.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    setCodes(null);
    try {
      const res = await api.post<any>('/auth/2fa/backup-codes/generate', {});
      if (res.success && res.data?.codes) {
        setCodes(res.data.codes);
        setShowCodes(true);
        loadStatus();
      } else {
        setError(res.error || 'Error al generar codigos');
      }
    } catch (err: any) {
      setError(err?.message || 'Error al generar codigos');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return null;

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <BackupIcon sx={{ fontSize: 28, mr: 1.5, color: 'warning.main' }} />
          <Box>
            <Typography variant="h6">Codigos de Respaldo</Typography>
            <Typography variant="body2" color="text.secondary">
              Codigos de emergencia para cuando no puedas recibir el codigo por correo o autenticador.
            </Typography>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {status?.hasBackupCodes ? (
          <Box>
            <Alert severity={status.remaining <= 2 ? 'warning' : 'info'} sx={{ mb: 2 }}>
              Tienes <strong>{status.remaining}</strong> de {status.total} codigos disponibles.
              {status.remaining <= 2 && ' Genere nuevos codigos pronto.'}
            </Alert>
          </Box>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No tiene codigos de respaldo. Genere codigos para tener acceso de emergencia si el correo no funciona.
          </Alert>
        )}

        {/* Show generated codes (only visible right after generation) */}
        {codes && showCodes && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Guarda estos codigos en un lugar seguro. No se mostraran de nuevo.
            </Alert>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
              {codes.map((code, i) => (
                <Typography key={i} variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600, textAlign: 'center', py: 0.5 }}>
                  {i + 1}. {code}
                </Typography>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'center' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={() => {
                  navigator.clipboard.writeText(codes.join('\n'));
                }}
              >
                Copiar
              </Button>
              <Button size="small" onClick={() => setShowCodes(false)}>
                Ocultar
              </Button>
            </Box>
          </Paper>
        )}

        <Button
          variant={status?.hasBackupCodes ? 'outlined' : 'contained'}
          startIcon={generating ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={handleGenerate}
          disabled={generating}
        >
          {status?.hasBackupCodes ? 'Regenerar Codigos' : 'Generar Codigos de Respaldo'}
        </Button>

        {status?.hasBackupCodes && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Al regenerar, los codigos anteriores se invalidan.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export { BackupCodesSection };
export default UserTwoFactorSetup;
